import { Table, ColumnSizingState, ColumnSizingInfoState } from '@tanstack/react-table'

/**
 * Default initial state for TanStack Table's column sizing info.
 * Shared across SimpleTable and TableLayout to keep the controlled
 * `columnSizingInfo` state in sync with the resize-persistence logic
 * (tracking which column is being resized via `resizingColumnRef`).
 */
export const INITIAL_COLUMN_SIZING_INFO: ColumnSizingInfoState = {
  startOffset: null,
  startSize: null,
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false,
  columnSizingStart: [],
}

// Measure actual scrollbar width (cached after first call)
let cachedScrollbarWidth: number | null = null
export function getScrollbarWidth(): number {
  if (cachedScrollbarWidth !== null) return cachedScrollbarWidth
  if (typeof document === `undefined`) return 0

  const outer = document.createElement(`div`)
  outer.style.visibility = `hidden`
  outer.style.overflow = `scroll`
  document.body.appendChild(outer)

  const inner = document.createElement(`div`)
  outer.appendChild(inner)

  cachedScrollbarWidth = outer.offsetWidth - inner.offsetWidth
  outer.remove()

  return cachedScrollbarWidth
}

type CalculateResult = {
  sizing: ColumnSizingState
  sizingToSave: ColumnSizingState
  lastColumnSavedWidth: number | null
}

/**
 * Calculates column widths for a table.
 *
 * First load (no saved settings): evenly distribute width, save all except last column.
 * Subsequent loads (has saved settings): use saved widths exactly, last column fills remaining space.
 *
 * @param table - The TanStack table instance
 * @param containerWidth - Available width for the table
 * @param savedSizing - Previously saved column widths (empty object if first load)
 * @param defaultMinSize - Default minimum column width (fallback for columns without minSize)
 * @param weights - First load splits the width by these instead of evenly; a column without one
 *   takes the average.
 */
export function calculateColumnWidths<T>(
  table: Table<T>,
  containerWidth: number,
  savedSizing: ColumnSizingState,
  defaultMinSize = 60,
  weights?: Record<string, number>,
): CalculateResult {
  // Account for the vertical scrollbar, the DataTable scroll container's 6px
  // right padding (`pr-1.5`, which keeps the last column off the scrollbar),
  // and 2px of subpixel rounding. Without the +6 the columns fill past the
  // visible content area and the table always shows a horizontal scrollbar
  // that can't be shrunk away.
  containerWidth -= getScrollbarWidth() + 8

  const columnOrder = table.getState().columnOrder
  const allColumns = table.getAllColumns()
  const existingColumnIds = new Set(allColumns.map((col) => col.id))
  const visibleColumns = columnOrder.filter((id) => existingColumnIds.has(id) && table.getColumn(id)?.getIsVisible())

  if (visibleColumns.length === 0 || containerWidth <= 0) {
    return { sizing: {}, sizingToSave: {}, lastColumnSavedWidth: null }
  }

  // Separate fixed and resizable columns
  const fixedColumns: string[] = []
  const resizableColumns: string[] = []
  let fixedWidth = 0

  for (const id of visibleColumns) {
    const col = table.getColumn(id)
    if (col?.columnDef.enableResizing === false) {
      fixedColumns.push(id)
      fixedWidth += col.columnDef.size ?? defaultMinSize
    } else {
      resizableColumns.push(id)
    }
  }

  const sizing: ColumnSizingState = {}
  const sizingToSave: ColumnSizingState = {}

  // Set fixed column widths
  for (const id of fixedColumns) {
    const col = table.getColumn(id)
    const width = col?.columnDef.size ?? defaultMinSize
    sizing[id] = width
    // Don't save fixed columns - they use their defined size
  }

  if (resizableColumns.length === 0) {
    return { sizing, sizingToSave, lastColumnSavedWidth: null }
  }

  const lastResizableColumn = resizableColumns[resizableColumns.length - 1]
  const hasSavedSizing = Object.keys(savedSizing).length > 0

  // Calculate widths for all columns except last
  let usedWidth = fixedWidth

  for (const id of resizableColumns) {
    if (id === lastResizableColumn) continue

    if (hasSavedSizing) {
      // Use saved width if available
      const savedWidth = savedSizing[id]
      if (savedWidth && savedWidth > 0) {
        sizing[id] = savedWidth
        sizingToSave[id] = savedWidth
        usedWidth += savedWidth
      } else {
        // Column not in saved settings (newly visible?) - give it default width
        const col = table.getColumn(id)
        const width = col?.columnDef.size ?? 250
        sizing[id] = width
        sizingToSave[id] = width
        usedWidth += width
      }
    } else {
      // First load: even distribution, or by weight when weights are given
      const availableWidth = containerWidth - fixedWidth
      const known = weights ? resizableColumns.map((columnId) => weights[columnId]).filter((w) => w != null) : []
      const fallback = known.length ? known.reduce((sum, w) => sum + w, 0) / known.length : 1
      const weightOf = (columnId: string) => weights?.[columnId] ?? fallback
      const totalWeight = resizableColumns.reduce((sum, columnId) => sum + weightOf(columnId), 0)
      const widthPerColumn = Math.floor((availableWidth * weightOf(id)) / totalWeight)
      const col = table.getColumn(id)
      const minSize = col?.columnDef.minSize ?? defaultMinSize
      const width = Math.max(widthPerColumn, minSize)
      sizing[id] = width
      sizingToSave[id] = width
      usedWidth += width
    }
  }

  // Last column fills remaining space (with minSize floor)
  const lastCol = table.getColumn(lastResizableColumn)
  const minSize = lastCol?.columnDef.minSize ?? defaultMinSize
  const remainingWidth = containerWidth - usedWidth
  const lastColSavedWidth = savedSizing[lastResizableColumn]

  if (lastColSavedWidth && lastColSavedWidth > 0) {
    const width = Math.max(lastColSavedWidth, remainingWidth, minSize)
    sizing[lastResizableColumn] = width
    sizingToSave[lastResizableColumn] = width
  } else {
    sizing[lastResizableColumn] = Math.max(remainingWidth, minSize)
  }

  return { sizing, sizingToSave, lastColumnSavedWidth: lastColSavedWidth ?? null }
}

/**
 * Calculates column widths to save after user resizes a column.
 * Saves all column widths except the last resizable column.
 */
export function getColumnWidthsToSave<T>(
  table: Table<T>,
  currentSizing: ColumnSizingState,
  includeLastColumn = false,
): ColumnSizingState {
  const columnOrder = table.getState().columnOrder
  const allColumns = table.getAllColumns()
  const existingColumnIds = new Set(allColumns.map((col) => col.id))
  const visibleColumns = columnOrder.filter((id) => existingColumnIds.has(id) && table.getColumn(id)?.getIsVisible())
  const resizableColumns = visibleColumns.filter((id) => table.getColumn(id)?.columnDef.enableResizing !== false)

  if (resizableColumns.length === 0) return {}

  const lastResizableColumn = resizableColumns[resizableColumns.length - 1]
  const sizingToSave: ColumnSizingState = {}

  for (const id of resizableColumns) {
    if (id === lastResizableColumn && !includeLastColumn) continue
    if (currentSizing[id]) {
      sizingToSave[id] = currentSizing[id]
    }
  }

  return sizingToSave
}

/**
 * Adjusts the last resizable column width during a column resize drag.
 * Used inside `onColumnSizingChange` in both SimpleTable and TableLayout to ensure
 * the last column always fills remaining space — while preserving any manual width
 * the user explicitly set by dragging the last column's resize handle.
 *
 * Mutates `newSizing` in place and returns the updated `lastColManualWidth`
 * for the caller to store back in its ref.
 *
 * @param table - The TanStack table instance
 * @param newSizing - The incoming sizing update (mutated in place with the adjusted last column width)
 * @param currentSizing - The current column sizing state before this update
 * @param containerWidth - The table container's client width
 * @param scrollbarOffset - Pixels to reserve for the vertical scrollbar (varies by table variant)
 * @param resizingColumn - The column ID currently being dragged, or `false` if none
 * @param lastColManualWidth - The previously stored manual width for the last column, or `null`
 * @returns The updated manual width to persist — equals the new width when the last column is
 *          being resized, or the unchanged `lastColManualWidth` otherwise
 */
export function adjustLastColumnWidth<T>(
  table: Table<T>,
  newSizing: ColumnSizingState,
  currentSizing: ColumnSizingState,
  containerWidth: number,
  scrollbarOffset: number,
  resizingColumn: string | false,
  lastColManualWidth: number | null,
): number | null {
  const colOrder = table.getState().columnOrder
  const visibleColumns = colOrder.filter((id) => table.getColumn(id)?.getIsVisible())
  const resizableColumns = visibleColumns.filter((id) => table.getColumn(id)?.columnDef.enableResizing !== false)

  if (resizableColumns.length === 0) return lastColManualWidth

  const lastCol = resizableColumns[resizableColumns.length - 1]
  const isResizingLastColumn = resizingColumn === lastCol
  let usedWidth = 0
  for (const id of visibleColumns) {
    if (id === lastCol) continue
    const col = table.getColumn(id)
    if (col?.columnDef.enableResizing === false) {
      usedWidth += col.columnDef.size ?? 60
    } else {
      usedWidth += newSizing[id] ?? currentSizing[id] ?? col?.getSize() ?? 250
    }
  }
  const minSize = table.getColumn(lastCol)?.columnDef.minSize ?? 60
  const fillWidth = Math.max(containerWidth - scrollbarOffset - usedWidth, minSize)

  if (isResizingLastColumn) {
    const requestedWidth = newSizing[lastCol] ?? currentSizing[lastCol] ?? fillWidth
    const finalWidth = Math.max(requestedWidth, fillWidth)
    newSizing[lastCol] = finalWidth
    return finalWidth
  }

  newSizing[lastCol] = lastColManualWidth !== null ? Math.max(lastColManualWidth, fillWidth) : fillWidth
  return lastColManualWidth
}
